import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Form, Spinner, Alert, ListGroup, Badge, Modal, Table } from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import ResearchLayout from '../../components/ResearchLayout';

const MedicalAdminAssignProtocolsToBatch = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [batch, setBatch] = useState(null);
  const [batches, setBatches] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [assignedProtocols, setAssignedProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showBatchSelection, setShowBatchSelection] = useState(!batchId);
  const [searchTerm, setSearchTerm] = useState('');
  // Changed from array to single selection for dropdown
  const [selectedProtocol, setSelectedProtocol] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // For remove protocol confirmation modal
  const [protocolToRemove, setProtocolToRemove] = useState(null);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [batchStatusFilter, setBatchStatusFilter] = useState('all');

  useEffect(() => {
    if (batchId) {
      loadBatchData();
      loadProtocols();
    } else {
      loadBatches();
    }
  }, [batchId]);

  const loadBatches = async () => {
    try {
      setLoading(true);
      // Medical admins can see ALL batches, not just their own
      const response = await api.get('/batches');
      console.log('Batches loaded:', response.data);
      setBatches(response.data.batches || []);
    } catch (error) {
      console.error('Error loading batches:', error);
      setError(`Failed to load batches: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadBatchData = async () => {
    try {
      const response = await api.get(`/batches/${batchId}`);
      setBatch(response.data.batch);
      
      // Get assigned protocols from the batch
      const protocolsResponse = await api.get(`/batches/${batchId}/protocols`);
      setAssignedProtocols(protocolsResponse.data.protocols || []);
    } catch (error) {
      console.error('Error loading batch:', error);
      setError('Failed to load batch information');
    }
  };

  const loadProtocols = async () => {
    try {
      const response = await api.get('/protocols');
      setProtocols(response.data.protocols || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading protocols:', error);
      setError('Failed to load protocols');
      setLoading(false);
    }
  };

  const loadAssignedProtocols = async () => {
    try {
      const response = await api.get(`/batches/${batchId}/protocols`);
      setAssignedProtocols(response.data.protocols || []);
    } catch (error) {
      console.error('Error loading assigned protocols:', error);
    }
  };

  useEffect(() => {
    if (batchId) {
      loadAssignedProtocols();
    }
  }, [batchId]);

  const handleBatchSelect = (selectedBatchId) => {
    navigate(`/medical-admin/batch/assign-protocols-to-batch/${selectedBatchId}`);
  };

  // Handle single protocol selection (dropdown style)
  const handleProtocolSelect = (protocolId) => {
    // If clicking the already selected protocol, deselect it
    if (selectedProtocol === protocolId) {
      setSelectedProtocol(null);
    } else {
      setSelectedProtocol(protocolId);
    }
  };

  const handleAssignProtocols = async () => {
    if (!selectedProtocol) {
      setError('Please select a protocol to assign');
      return;
    }
    
    try {
      setSaving(true);
      setError(null);

      // Use the new batch protocols assignment endpoint
      const response = await api.post(`/batches/${batchId}/protocols`, {
        protocol_ids: [selectedProtocol]
      });

      setSuccess(`Protocol assigned successfully!`);
      setSelectedProtocol(null);
      setShowConfirmModal(false);
      
      // Reload assigned protocols
      loadAssignedProtocols();
    } catch (error) {
      console.error('Error assigning protocol:', error);
      setError('Failed to assign protocol. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveProtocol = async (protocolId) => {
    setProtocolToRemove(protocolId);
    setShowRemoveModal(true);
  };

  const confirmRemoveProtocol = async () => {
    if (!protocolToRemove) return;

    try {
      await api.delete(`/batches/${batchId}/protocols/${protocolToRemove}`);
      setSuccess('Protocol removed successfully!');
      setShowRemoveModal(false);
      setProtocolToRemove(null);
      loadAssignedProtocols();
    } catch (error) {
      console.error('Error removing protocol:', error);
      setError('Failed to remove protocol');
    }
  };

  const getBatchStatus = (batch) => {
    if (batch.is_expired) {
      return { text: 'Expired', class: 'bg-danger' };
    } else if (batch.is_active) {
      return { text: 'Active', class: 'bg-success' };
    } else {
      return { text: 'Scheduled', class: 'bg-warning' };
    }
  };

  const filteredBatches = batches.filter(batch => {
    if (batchStatusFilter === 'all') return true;
    if (batchStatusFilter === 'active') return batch.is_active;
    if (batchStatusFilter === 'scheduled') return !batch.is_active && !batch.is_expired;
    if (batchStatusFilter === 'expired') return batch.is_expired;
    return true;
  });

  const filteredProtocols = protocols.filter(protocol =>
    protocol.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (protocol.description && protocol.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <ResearchLayout>
        <div className="text-center mt-5">
          <Spinner animation="border" />
          <p className="mt-2">Loading...</p>
        </div>
      </ResearchLayout>
    );
  }

  // Show batch selection if no batchId
  if (!batchId) {
    return (
      <ResearchLayout>
        <div className="container-fluid">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2>Assign Protocols to Batch</h2>
              <p className="text-muted mb-0">Select a batch to assign protocols</p>
            </div>
            <Button 
              variant="secondary" 
              onClick={() => navigate('/medical-admin/dashboard')}
            >
              Back to Dashboard
            </Button>
          </div>

          {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

          <Card>
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">
                <i className="fas fa-folder-open me-2"></i>
                Select a Batch to Assign Protocols
              </h5>
            </Card.Header>
            <Card.Body>
              {/* Filter Controls */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <Form.Control
                    type="text"
                    placeholder="Search batches by name or description..."
                    onChange={(e) => {
                      const search = e.target.value.toLowerCase();
                      setSearchTerm(search);
                    }}
                  />
                </div>
                <div className="col-md-3">
                  <Form.Select
                    value={batchStatusFilter}
                    onChange={(e) => setBatchStatusFilter(e.target.value)}
                  >
                    <option value="all">All Batches</option>
                    <option value="active">Active Batches</option>
                    <option value="scheduled">Scheduled Batches</option>
                    <option value="expired">Expired Batches</option>
                  </Form.Select>
                </div>
                <div className="col-md-3 text-end">
                  <div className="badge bg-primary fs-6">
                    {filteredBatches.length} of {batches.length} batches
                  </div>
                </div>
              </div>

              {batches.length === 0 ? (
                <div className="text-center py-5">
                  <i className="fas fa-folder-open fa-4x text-muted mb-4"></i>
                  <h4 className="text-muted">No Batches Found</h4>
                  <p className="text-muted">
                    You haven't created any batches yet. Create a batch first to assign protocols.
                  </p>
                  <Button
                    variant="primary"
                    onClick={() => navigate('/medical-admin/batch')}
                  >
                    <i className="fas fa-plus me-2"></i>
                    Create New Batch
                  </Button>
                </div>
              ) : filteredBatches.length === 0 ? (
                <div className="text-center py-5">
                  <i className="fas fa-filter fa-3x text-muted mb-3"></i>
                  <h5 className="text-muted">No batches match your filter</h5>
                  <p className="text-muted">
                    Try adjusting the status filter or search criteria.
                  </p>
                </div>
              ) : (
                <div className="table-responsive">
<Table hover>
                    <thead>
                      <tr>
                        <th>Batch Name</th>
                        <th>Status</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Cases</th>
                        <th>Protocols</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredBatches.map(batch => {
                        const status = getBatchStatus(batch);
                        return (
                          <tr key={batch.id}>
                            <td>
                              <div>
                                <strong>{batch.name}</strong>
                                {batch.description && (
                                  <p className="text-muted mb-0 small">{batch.description}</p>
                                )}
                              </div>
                            </td>
                            <td>
                              <Badge className={status.class}>
                                {status.text}
                              </Badge>
                            </td>
                            <td>{formatDate(batch.start_date)}</td>
                            <td>{formatDate(batch.end_date)}</td>
                            <td>
                              <Badge bg="info">{batch.case_count || 0} cases</Badge>
                            </td>
                            <td>
                              <Badge bg="warning">{batch.protocol_count || 0} protocols</Badge>
                            </td>
                            <td>
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleBatchSelect(batch.id)}
                              >
                                <i className="fas fa-clipboard-list me-1"></i>
                                Assign Protocols
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>
      </ResearchLayout>
    );
  }

  return (
    <ResearchLayout>
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h2>Assign Protocols to Batch</h2>
            <p className="text-muted mb-0">
              Batch: <strong>{batch?.name}</strong>
            </p>
          </div>
          <div>
            <Button 
              variant="secondary" 
              className="me-2"
              onClick={() => navigate('/medical-admin/batch/assign-protocols')}
            >
              <i className="fas fa-arrow-left me-1"></i>
              Back to Batch Selection
            </Button>
            <Button 
              variant="secondary" 
              onClick={() => navigate('/medical-admin/dashboard')}
            >
              Back to Dashboard
            </Button>
          </div>
        </div>

        {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}
        {success && <Alert variant="success" onClose={() => setSuccess(null)} dismissible>{success}</Alert>}

        <div className="row">
          {/* Protocol Selection - Dropdown for Single Selection */}
          <div className="col-md-8">
            <Card>
              <Card.Header>
                <h5 className="mb-0">Select Protocol to Assign</h5>
                <p className="text-muted mb-0">
                  Choose one protocol to assign to the batch
                </p>
              </Card.Header>
              <Card.Body>
                {/* Protocol Dropdown */}
                <Form.Group className="mb-3">
                  <Form.Label>Available Protocols</Form.Label>
                  <Form.Select
                    value={selectedProtocol || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedProtocol(value ? parseInt(value) : null);
                    }}
                    disabled={assignedProtocols.length > 0}
                  >
                    <option value="">-- Select a Protocol --</option>
                    {assignedProtocols.length === 0 && filteredProtocols
                      .filter(protocol => !assignedProtocols.some(ap => ap.protocol_id === protocol.id))
                      .map(protocol => (
                      <option key={protocol.id} value={protocol.id}>
                        {protocol.name} ({protocol.question_count} questions)
                      </option>
                    ))}
                  </Form.Select>
                  {assignedProtocols.length > 0 ? (
                    <Form.Text className="text-danger">
                      This batch already has a protocol assigned. Delete it first to assign a new one.
                    </Form.Text>
                  ) : (
                    <Form.Text className="text-muted">
                      Select one protocol to assign to the batch
                    </Form.Text>
                  )}
                </Form.Group>

                {/* Show Selected Protocol Details */}
                {selectedProtocol && (
                  <div className="mt-3 p-3 bg-light rounded">
                    <h6>Selected Protocol:</h6>
                    {(() => {
                      const protocol = protocols.find(p => p.id === selectedProtocol);
                      return protocol ? (
                        <div>
                          <strong>{protocol.name}</strong>
                          {protocol.description && (
                            <p className="text-muted mb-1 small">{protocol.description}</p>
                          )}
                          <Badge bg="secondary">Questions: {protocol.question_count}</Badge>
                        </div>
                      ) : null;
                    })()}
                  </div>
                )}

                {/* Assign Button - Only enabled when no protocol assigned */}
                {assignedProtocols.length === 0 && selectedProtocol && (
                  <div className="mt-3 text-center">
                    <Button
                      variant="primary"
                      onClick={() => setShowConfirmModal(true)}
                    >
                      <i className="fas fa-plus me-1"></i>
                      Assign Selected Protocol
                    </Button>
                  </div>
                )}
                
                {assignedProtocols.length > 0 && (
                  <div className="mt-3 text-center">
                    <p className="text-muted mb-2">
                      <i className="fas fa-info-circle me-1"></i>
                      This batch already has a protocol assigned. Delete it first to assign a new one.
                    </p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>

          {/* Assigned Protocols */}
          <div className="col-md-4">
            <Card>
              <Card.Header>
                <h5 className="mb-0">Assigned Protocols ({assignedProtocols.length})</h5>
              </Card.Header>
              <Card.Body>
                {assignedProtocols.length === 0 ? (
                  <p className="text-muted">No protocols assigned to this batch yet.</p>
                ) : (
                  <ListGroup variant="flush">
                    {assignedProtocols.map(assignment => (
                      <ListGroup.Item key={assignment.id} className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{assignment.protocol_name}</strong>
                          <p className="text-muted mb-0 small">
                            Assigned: {formatDate(assignment.assigned_at)}
                          </p>
                        </div>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleRemoveProtocol(assignment.protocol_id)}
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
              </Card.Body>
            </Card>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Assign Protocols to Batch</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to assign this protocol to the batch "{batch?.name}"?</p>
          <p className="text-muted">
            This will add the selected protocol to the batch. Doctors assigned to this batch will be able to work on cases using this protocol.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleAssignProtocols}
            disabled={saving}
          >
            {saving ? 'Assigning...' : 'Assign Protocols'}
          </Button>
        </Modal.Footer>
      </Modal>
      
      {/* Remove Protocol Confirmation Modal */}
      <Modal show={showRemoveModal} onHide={() => setShowRemoveModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Remove Protocol from Batch</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to remove this protocol from the batch?</p>
          <p className="text-muted">
            This will remove the protocol from the batch. Doctors assigned to this batch will no longer be able to work on cases using this protocol.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRemoveModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="danger" 
            onClick={confirmRemoveProtocol}
            disabled={saving}
          >
            {saving ? 'Removing...' : 'Remove Protocol'}
          </Button>
        </Modal.Footer>
      </Modal>
    </ResearchLayout>
  );
};

export default MedicalAdminAssignProtocolsToBatch;